<template>
  <div class="orders font-inter pb-10 mb-10 lg:py-6 enter-content-animation px-4 md:px-0 sm:pt-10 pt-4" v-if="!selectedOrder">
    <div class="mb-6 flex items-center gap-4">
        <h1 class="text-2xl font-bold">Pedidos em andamento</h1>
    </div>
    <div v-if="orders && orders.length" class="orders__orders-list grid grid-cols-12 gap-4">
        <div class="box-shadow p-6 rounded-2xl col-span-12 sm:col-span-6 lg:col-span-4 relative border bg-white hover:border-primary transition duration-300" v-for="order in orders" :key="order.id">
            <div :style="{background: statusTranslations[order.status]?.color}" class="absolute top-0 right-0 text-white text-[10px] uppercase font-bold p-2 px-3 rounded-tr-xl rounded-bl-xl">
                {{ statusTranslations[order.status]?.label }}
            </div>
            
            <div class="flex items-center gap-4 mb-4">
                <div class="h-16 w-16 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden border">
                    <img 
                        v-if="order.items?.[0]?.product?.media?.[0]" 
                        class="h-full w-full object-cover" 
                        :src="apiBaseUrl + '/storage/' + order.items[0].product.media[0].path" 
                        @error="onOrderImageError"
                    >
                    <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" class="text-gray-400"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
                </div>
                <div>
                    <h5 class="text-semibold text-base font-bold text-gray-800 line-clamp-1">{{ order.items?.[0]?.product?.name || 'Pedido sem itens' }}</h5>
                    <p class="text-xs text-gray-400">#{{ order.id }}, às {{ new Date(order.createdAt).toLocaleTimeString('pt-BR').slice(0, 5) }}h</p>
                </div>
            </div>

            <div class="flex flex-col gap-3 py-2 border-t border-dashed mt-2">
                <div class="flex items-center justify-between">
                    <div class="flex items-center gap-2 text-gray-600">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m7.5 4.27 9 5.15"/><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/></svg>
                        <span class="text-sm font-medium">{{ order.items?.length || 0 }} {{ (order.items?.length || 0) > 1 ? 'itens' : 'item' }}</span>
                    </div>
                    <div class="font-bold text-primary">{{ order.total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}</div>
                </div>
                
                <div v-if="order.tab" class="flex items-center gap-2 text-gray-600">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 3h18v18H3z"/><path d="M3 9h18"/><path d="M9 3v18"/></svg>
                    <p class="text-sm">Mesa {{ order.tab.number }} <span v-if="order.tab.name" class="text-xs opacity-60">({{ order.tab.name }})</span></p>
                </div>

                <div class="flex items-center justify-between mt-2">
                    <div class="text-xs flex items-center gap-2 text-gray-400 uppercase font-bold">
                        <svg v-if="order.type === 'delivery'" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="1" y="3" width="15" height="13"/><polyline points="16 8 20 8 23 11 23 16 16 16"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
                        <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                        <span>{{ orderTypeTranslations[order.type] }}</span>
                    </div>
                    <button @click="selectedOrder = order" class="text-xs text-primary font-bold flex items-center gap-1 hover:underline transition transition-all duration-200">
                        DETALHES
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                    </button>
                </div>
            </div>
        </div>
    </div>
    <div class="pt-20 flex flex-col items-center justify-center opacity-40" v-else>
        <svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="mb-4"><path d="M3 7V5c0-1.1.9-2 2-2h2"/><path d="M17 3h2c1.1 0 2 .9 2 2v2"/><path d="M21 17v2c0 1.1-.9 2-2 2h-2"/><path d="M7 21H5c-1.1 0-2-.9-2-2v-2"/><circle cx="12" cy="12" r="3"/><path d="m16 16-1.9-1.9"/></svg>
        <h5 class="font-semibold text-lg text-center">Nenhum pedido em andamento</h5>
        <p class="text-center text-sm">Os pedidos aparecerão aqui assim que forem feitos.</p>
    </div>
  </div>
  <OrdersShow :order="selectedOrder" :setOrder="(value: any) => selectedOrder = value" v-else/>
</template>

<script lang="ts" setup>
import axiosClient from '@/api/axios';
import { onMounted, ref, watch } from 'vue';
import { apiBaseUrl, orders } from '@/global/state/GlobalState';
import OrdersShow from './OrdersShow.vue'

const props = defineProps<{id?: number}>()

const selectedOrder = ref<any>(null)
const statusTranslations: any = {
    'PENDING': {label: 'pendente', color:'#918373'},
    'PREPARING': {label: 'preparando', color:'#FF9B3D'},
    'PREPARED': {label: 'preparado', color:'#59A6F8'},
    'DELIVERED': {label: 'entregue', color: '#00c9a7'},
    'CANCELED': {label: 'cancelado', color:'#F83535'},
    'COMPLETED': {label: 'concluído', color: '#00c9a7'},
}
const orderTypeTranslations: any = {
    'delivery': 'entrega',
    'waiter': 'presencial',
    'pick_up': 'retirada',
}

async function getOrders(){
    try {
        const res = await axiosClient.get('/orders')
        const fetchedOrders = res.data.data || res.data
        
        if (Array.isArray(fetchedOrders)) {
            orders.value = fetchedOrders
            
            if(props.id){
                selectedOrder.value = fetchedOrders.find((o: any) => o.id == props.id)
            }
        }
    } catch (error) {
        console.error('Error fetching orders:', error)
    }
}

function onOrderImageError(e: any){
    e.target.style.display = 'none'
}

onMounted(() => {
  getOrders()
})

watch(() => props.id, (newId) => {
    if (newId) {
        selectedOrder.value = orders.value.find((o: any) => o.id == newId)
    }
})
</script>

<style scoped>
.box-shadow {
    box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
}
.line-clamp-1 {
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
</style>